import { type FC, type ReactNode } from "react";
import { type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { DateFormatType } from "~/common/utils/DateFormatter";
import { type RowProps } from "~/features/orders/orderList/components/OrderLineTable";
import { OrderLineItemElements } from "~/features/orders/common/components/OrderLineItemElements";
import { IconButton, Stack, Typography } from "~/common/mui";
import { Param, TooltipParam } from "../common/components/v2/Params";
import { formatShippingOrDeliveryDate } from "~/common/utils/dateUtils";
import Tag from "~/common/components/Tag/Tag";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { OrderSubLineGrid, OrderSubLineListDivider } from "../common/components/v2/OrderAndQuoteSharedStyles";
import FileDownloadPopup from "./FileDowloadPopup";
import { useTranslation } from "react-i18next";
import { TrackerV2 } from "../orderDetailsV2/OrderDetailsV2/TrackerV2";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";

interface Props extends RowProps {
  index: number;
  subEntry: OrderLineItemData;
  showDownload: boolean;
  onOpenDownload: () => void;
  onCloseDownload: () => void;
  isLast?: boolean;
}

const OrderSubLineListRow: FC<Props> = ({ index, subEntry, showDownload, onOpenDownload, onCloseDownload, isLast }) => {
  const { t } = useTranslation();
  const showTrackerInfo =
    !!subEntry.invoiceData &&
    (subEntry.invoiceData.trackingNo !== "-" ||
      (subEntry.invoiceData.shippingPartnerName && subEntry.invoiceData.shippingPartnerName !== "-") ||
      (subEntry.invoiceData.shippingPartnerId && subEntry.invoiceData.shippingPartnerId !== "-"));

  return (
    <>
      <OrderSubLineGrid key={index}>
        <Param label={t("order.deliveries.label")} value={subEntry.lineNo} />
        <TooltipParam
          label={t("quoteListPage.table.header.confirmedQuantity")}
          value={t("common.of.text.with.values", {
            firstValue: subEntry.confirmedQty,
            secondValue: subEntry.qty,
          })}
          tooltipContent={
            <Stack gap={0.5}>
              <Param label={t("quoteListPage.table.header.confirmedQuantity")} value={subEntry.confirmedQty} />
              <Param label={t("quoteListPage.table.header.requestedQuantity")} value={subEntry.qty} />
            </Stack>
          }
        />
        <Param
          label={t("quoteListPage.table.header.estimatedDeliveryDate")}
          value={formatShippingOrDeliveryDate(subEntry.deliveryDate, DateFormatType.DATE)}
        />
        <Param label={t("quoteDetailsPage.table.header.status")} value={<Tag>{t(subEntry.status)}</Tag>} />
        <Param
          label={t("orderListPage.table.header.total")}
          value={
            <Typography variant="bodyLarge" color="textSecondary">
              <OrderLineItemElements.TotalPrice isBold={false} data={subEntry} />
            </Typography>
          }
        />
        {subEntry.invoiceData && (
          <TooltipWithArrow placement="top" title={t("orderDetails.download.text")}>
            <IconButton
              sx={{
                height: "40px",
              }}
              onClick={onOpenDownload}
            >
              <FileDownloadOutlinedIcon color="primary" />
            </IconButton>
          </TooltipWithArrow>
        )}

        {subEntry.invoiceData && (
          <Param label={t("quoteDetailsPage.summary.invoiceDate")} value={subEntry.invoiceData.invoiceDate} />
        )}
        {showTrackerInfo && subEntry.invoiceData && (
          <Param
            label={t("quoteDetailsPage.summary.shipmentReference")}
            value={<TrackerV2 data={subEntry.invoiceData} />}
          />
        )}
      </OrderSubLineGrid>
      {!isLast && <OrderSubLineListDivider />}

      {showDownload && subEntry.invoiceData && (
        <FileDownloadPopup invoiceData={subEntry.invoiceData} onCloseDownload={onCloseDownload} />
      )}
    </>
  );
};

export default OrderSubLineListRow;

export interface GridDataItem {
  label: ReactNode;
  value: JSX.Element | string | undefined;
}
